// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import equal from "fast-deep-equal";
import { ChangeEvent, useCallback, useRef, useEffect, useState } from "react";
import { User, getApi } from "./store/api";
import { fork, clearArray, CancelError, Action } from "./util";

export function getRoot() {
  return document.getElementById("root");
}

export function useWidthGate(gates: { [key: string]: number }): number {
  const [widthBucket, setWidthBucket] = useState(0);
  useEffect(() => {
    function setter() {
      const bucket = Object.values(gates).reduce(
        (acc, x) => (x > window.innerWidth ? acc : Math.max(x, acc)),
        0
      );
      update("widthBucket", widthBucket, setWidthBucket, bucket);
    }
    setter();
    window.addEventListener("resize", setter);
    return () => window.removeEventListener("resize", setter);
  });
  return widthBucket;
}

export enum Lifecycle {
  VIEWED,
  EDITED,
  SAVING,
  SAVED,
}

export function scrollIntoViewSoon(elem: HTMLElement | null, options?: object) {
  if (elem != null) {
    setTimeout(
      () =>
        elem.scrollIntoView(options || { behavior: "smooth", block: "start" }),
      100
    );
  } else {
    console.error("reveal: no element to scroll");
  }
}

export type TextInputEvent = ChangeEvent<HTMLInputElement>;
export type TextAreaEvent = ChangeEvent<HTMLTextAreaElement>;
export type Setter<T> = (x: T) => any;
export type Getter<T> = T;

export function textInputHandler(setter: Setter<string>) {
  return (e: TextInputEvent | TextAreaEvent) => setter(e.target.value);
}

export function useAuthLoader(
  load: (user: User | null) => Promise<void>
): User | null {
  const api = getApi();
  const [user, setUser] = useState<User | null>(null);

  // TODO: cancel load call if useEffect unsubscribes
  const wrapLoad = (user: User | null) => {
    setUser(user);
    fork(() => load(user));
  };

  const memoLoad = useCallback(wrapLoad, [user]);
  const userUid = user == null ? "" : user.uid;

  useEffect(
    () => api.subscribeAuthChanges(memoLoad),
    // eslint-disable-next-line
    [userUid]
  );

  return user;
}

export function useAsyncGuard() {
  const trackerRef = useRef<CancelTracker>(new CancelTracker());
  const tracker = trackerRef.current;
  const cancel = tracker.cancel;

  useEffect(() => cancel, [cancel]);

  function guard<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const cancel = () => reject(new CancelError());
      tracker.add(cancel);
      promise.then(resolve).catch(reject);
    });
  }

  return guard;
}

type Reject = (err: Error) => void;

class CancelTracker {
  readonly rejects: Reject[];
  canceled: boolean;

  constructor() {
    this.rejects = [];
    this.canceled = false;
  }

  public cancel = () => {
    this.canceled = true;
    this.rejects.forEach(this.call);
    clearArray(this.rejects);
  };

  public add(reject: Reject): void {
    if (this.canceled) {
      this.call(reject);
    } else {
      this.rejects.push(reject);
    }
  }

  private call = (reject: Reject) => {
    reject(new CancelError("Operation Canceled"));
  };
}

export function update<T>(
  label: string,
  current: T,
  set: Setter<T>,
  value: T
): boolean {
  if (!equal(current, value)) {
    debug(`update ${label} after`, value);
    set(value);
    debug(`done setting ${label}`);
    return true;
  } else {
    return false;
  }
}

export function observe<T>(
  observer: Action,
  state: [Getter<T>, Setter<T>]
): [Getter<T>, Setter<T>] {
  const [getter, setter] = state;
  return [
    getter,
    (value: T) => {
      observer();
      setter(value);
    },
  ];
}

function debug(message: string, state?: any) {
  if (true) {
    console.log(`UiUtil: ${message}`);
    if (state != null) {
      console.log(state);
    }
  }
}
